/**
 * Backgammon Game Engine
 *
 * Core game logic: board setup, dice rolling, move validation (bar entry,
 * blocking, bearing off), move execution, turn management, game-over detection.
 *
 * Board: array of 26 integers. Index 0 = P1 bar, 1-24 = points, 25 = P2 bar.
 * Positive = Player 1 checkers, Negative = Player 2 checkers.
 * Player 1 moves from high points toward point 1 (home board 1-6).
 * Player 2 moves from low points toward point 24 (home board 19-24).
 */

import {
  PlayerSide,
  BackgammonBoard,
  BackgammonMove,
  DiceRoll,
  DieValue,
  MoveResult,
  BackgammonGameState,
  BackgammonGameSettings,
  INITIAL_BOARD,
  CHECKERS_PER_PLAYER,
  P1_BAR,
  P2_BAR,
} from './types';

export class BackgammonEngine {
  private state: BackgammonGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  initializeGame(_settings: BackgammonGameSettings): void {
    this.state = {
      board: [...INITIAL_BOARD],
      currentPlayer: 'player1',
      dice: null,
      moveHistory: [],
      gameStatus: 'rolling',
      borneOff: { player1: 0, player2: 0 },
      turnNumber: 0,
      gameId: `backgammon-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      startedAt: Date.now(),
      selectedPoint: null,
      validMoves: [],
    };
  }

  // =========================================================================
  // Dice
  // =========================================================================

  rollDice(): DiceRoll {
    const die1 = (Math.floor(Math.random() * 6) + 1) as DieValue;
    const die2 = (Math.floor(Math.random() * 6) + 1) as DieValue;
    const isDoubles = die1 === die2;

    const movesRemaining: DieValue[] = isDoubles
      ? [die1, die1, die1, die1]
      : [die1, die2];

    const dice: DiceRoll = { die1, die2, isDoubles, movesRemaining };
    this.state.dice = dice;
    this.state.gameStatus = 'moving';
    this.state.validMoves = this.computeAllValidMoves();

    // If no valid moves, auto-advance turn
    if (this.state.validMoves.length === 0) {
      this.state.gameStatus = 'rolling';
    }

    return dice;
  }

  /** Set dice to specific values (for testing or multiplayer sync) */
  setDice(die1: DieValue, die2: DieValue): DiceRoll {
    const isDoubles = die1 === die2;
    const movesRemaining: DieValue[] = isDoubles
      ? [die1, die1, die1, die1]
      : [die1, die2];

    const dice: DiceRoll = { die1, die2, isDoubles, movesRemaining };
    this.state.dice = dice;
    this.state.gameStatus = 'moving';
    this.state.validMoves = this.computeAllValidMoves();

    if (this.state.validMoves.length === 0) {
      this.state.gameStatus = 'rolling';
    }

    return dice;
  }

  // =========================================================================
  // Move Validation
  // =========================================================================

  getValidMoves(): BackgammonMove[] {
    return [...this.state.validMoves];
  }

  getValidMovesForPoint(fromPoint: number): BackgammonMove[] {
    return this.state.validMoves.filter(m => m.from === fromPoint);
  }

  /**
   * Compute all valid moves for the current player given remaining dice.
   */
  private computeAllValidMoves(): BackgammonMove[] {
    if (!this.state.dice || this.state.dice.movesRemaining.length === 0) {
      return [];
    }

    const player = this.state.currentPlayer;
    const board = this.state.board;
    const remaining = this.state.dice.movesRemaining;
    const barIndex = player === 'player1' ? P1_BAR : P2_BAR;
    const hasCheckersOnBar = board[barIndex] !== 0;

    const moves: BackgammonMove[] = [];
    const uniqueDice = [...new Set(remaining)];

    for (const die of uniqueDice) {
      if (hasCheckersOnBar) {
        // Must enter from bar first
        const barMove = this.getBarEntryMove(player, die);
        if (barMove) moves.push(barMove);
      } else {
        // Normal moves + bearing off
        const pointMoves = this.getMovesForDie(player, board, die);
        moves.push(...pointMoves);
      }
    }

    return moves;
  }

  private getBarEntryMove(player: PlayerSide, die: DieValue): BackgammonMove | null {
    const board = this.state.board;

    if (player === 'player1') {
      // P1 enters from bar to opponent's home board (points 25 - die = target)
      const target = 25 - die; // die=1 → point 24, die=6 → point 19
      if (this.canLandOn(player, board, target)) {
        const isHit = board[target] < 0; // opponent checker
        return {
          from: P1_BAR,
          to: target,
          dieValue: die,
          isHit,
          isBearOff: false,
          isBarEntry: true,
        };
      }
    } else {
      // P2 enters from bar to opponent's home board (points die)
      const target = die; // die=1 → point 1, die=6 → point 6
      if (this.canLandOn(player, board, target)) {
        const isHit = board[target] > 0; // opponent checker
        return {
          from: P2_BAR,
          to: target,
          dieValue: die,
          isHit,
          isBearOff: false,
          isBarEntry: true,
        };
      }
    }

    return null;
  }

  private getMovesForDie(
    player: PlayerSide,
    board: BackgammonBoard,
    die: DieValue
  ): BackgammonMove[] {
    const moves: BackgammonMove[] = [];
    const canBearOff = this.canBearOff(player, board);

    for (let point = 1; point <= 24; point++) {
      if (!this.hasCheckerAt(player, board, point)) continue;

      if (player === 'player1') {
        const target = point - die;

        if (target >= 1) {
          // Normal move
          if (this.canLandOn(player, board, target)) {
            const isHit = board[target] < 0;
            moves.push({
              from: point,
              to: target,
              dieValue: die,
              isHit,
              isBearOff: false,
              isBarEntry: false,
            });
          }
        } else if (target === 0 && canBearOff) {
          // Exact bear off
          moves.push({
            from: point,
            to: 0,
            dieValue: die,
            isHit: false,
            isBearOff: true,
            isBarEntry: false,
          });
        } else if (target < 0 && canBearOff) {
          // Bear off with higher die — only if no checker on a higher point
          if (this.isHighestCheckerInHomeBoard(player, board, point)) {
            moves.push({
              from: point,
              to: 0,
              dieValue: die,
              isHit: false,
              isBearOff: true,
              isBarEntry: false,
            });
          }
        }
      } else {
        // Player 2: moves from low to high
        const target = point + die;

        if (target <= 24) {
          if (this.canLandOn(player, board, target)) {
            const isHit = board[target] > 0;
            moves.push({
              from: point,
              to: target,
              dieValue: die,
              isHit,
              isBearOff: false,
              isBarEntry: false,
            });
          }
        } else if (target === 25 && canBearOff) {
          // Exact bear off
          moves.push({
            from: point,
            to: 25,
            dieValue: die,
            isHit: false,
            isBearOff: true,
            isBarEntry: false,
          });
        } else if (target > 25 && canBearOff) {
          // Bear off with higher die
          if (this.isHighestCheckerInHomeBoard(player, board, point)) {
            moves.push({
              from: point,
              to: 25,
              dieValue: die,
              isHit: false,
              isBearOff: true,
              isBarEntry: false,
            });
          }
        }
      }
    }

    return moves;
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  executeMove(move: BackgammonMove): MoveResult {
    if (this.state.gameStatus !== 'moving') {
      throw new Error('Not in moving state');
    }

    // Validate the move is in valid moves list
    const isValid = this.state.validMoves.some(
      m => m.from === move.from && m.to === move.to && m.dieValue === move.dieValue
    );
    if (!isValid) {
      throw new Error('Invalid move');
    }

    const board = [...this.state.board];
    const player = this.state.currentPlayer;
    let capturedToBar = false;

    // Remove checker from source
    if (move.isBarEntry) {
      const barIndex = player === 'player1' ? P1_BAR : P2_BAR;
      if (player === 'player1') {
        board[barIndex]--;
      } else {
        board[barIndex]++;
      }
    } else {
      if (player === 'player1') {
        board[move.from]--;
      } else {
        board[move.from]++;
      }
    }

    // Handle destination
    if (move.isBearOff) {
      if (player === 'player1') {
        this.state.borneOff.player1++;
      } else {
        this.state.borneOff.player2++;
      }
    } else {
      // Check for hit
      if (move.isHit) {
        capturedToBar = true;
        if (player === 'player1') {
          // Hit P2 checker — send to P2 bar
          board[move.to] = 0; // Remove opponent's single checker
          board[P2_BAR]--; // P2 bar uses negative numbers
        } else {
          // Hit P1 checker — send to P1 bar
          board[move.to] = 0;
          board[P1_BAR]++; // P1 bar uses positive numbers
        }
      }

      // Place checker at destination
      if (player === 'player1') {
        board[move.to]++;
      } else {
        board[move.to]--;
      }
    }

    // Update board
    this.state.board = board;

    // Consume the die
    const diceIdx = this.state.dice!.movesRemaining.indexOf(move.dieValue);
    if (diceIdx !== -1) {
      this.state.dice!.movesRemaining.splice(diceIdx, 1);
    }

    // Record move in current turn
    if (this.state.moveHistory.length === 0 || this.state.moveHistory.length <= this.state.turnNumber) {
      this.state.moveHistory.push([move]);
    } else {
      this.state.moveHistory[this.state.turnNumber].push(move);
    }

    // Recalculate valid moves
    this.state.validMoves = this.computeAllValidMoves();
    this.state.selectedPoint = null;

    // Check if turn is over
    if (
      this.state.dice!.movesRemaining.length === 0 ||
      this.state.validMoves.length === 0
    ) {
      this.state.gameStatus = 'rolling';
    }

    return { move, newBoard: [...board], capturedToBar };
  }

  // =========================================================================
  // Turn Management
  // =========================================================================

  advanceTurn(): void {
    if (this.state.gameStatus !== 'rolling') return;

    // Check for game over
    if (this.isGameOver()) {
      this.state.gameStatus = 'completed';
      this.state.winner = this.getWinner()!;
      return;
    }

    this.state.currentPlayer =
      this.state.currentPlayer === 'player1' ? 'player2' : 'player1';
    this.state.turnNumber++;
    this.state.dice = null;
    this.state.validMoves = [];
    this.state.selectedPoint = null;
  }

  // =========================================================================
  // Game Over
  // =========================================================================

  isGameOver(): boolean {
    return (
      this.state.borneOff.player1 === CHECKERS_PER_PLAYER ||
      this.state.borneOff.player2 === CHECKERS_PER_PLAYER
    );
  }

  getWinner(): PlayerSide | undefined {
    if (this.state.borneOff.player1 === CHECKERS_PER_PLAYER) return 'player1';
    if (this.state.borneOff.player2 === CHECKERS_PER_PLAYER) return 'player2';
    return undefined;
  }

  /** Check if the win is a gammon (opponent has 0 borne off) */
  isGammon(): boolean {
    const winner = this.getWinner();
    if (!winner) return false;
    const loser = winner === 'player1' ? 'player2' : 'player1';
    return this.state.borneOff[loser] === 0;
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  getBoard(): BackgammonBoard {
    return [...this.state.board];
  }

  getCurrentPlayer(): PlayerSide {
    return this.state.currentPlayer;
  }

  getDice(): DiceRoll | null {
    return this.state.dice ? { ...this.state.dice, movesRemaining: [...this.state.dice.movesRemaining] } : null;
  }

  getGameState(): BackgammonGameState {
    return {
      ...this.state,
      board: [...this.state.board],
      dice: this.state.dice
        ? { ...this.state.dice, movesRemaining: [...this.state.dice.movesRemaining] }
        : null,
      moveHistory: this.state.moveHistory.map(turn => [...turn]),
      borneOff: { ...this.state.borneOff },
      validMoves: [...this.state.validMoves],
    };
  }

  setSelectedPoint(point: number | null): void {
    this.state.selectedPoint = point;
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  serialize(): string {
    return JSON.stringify(this.state);
  }

  deserialize(json: string): BackgammonGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);
      if (!parsed.board || !Array.isArray(parsed.board) || parsed.board.length !== 26) {
        return { error: 'Invalid state: board must be array of 26' };
      }
      if (!['player1', 'player2'].includes(parsed.currentPlayer)) {
        return { error: 'Invalid state: invalid currentPlayer' };
      }
      return parsed as BackgammonGameState;
    } catch (e) {
      return { error: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  setGameState(state: BackgammonGameState): void {
    this.state = {
      ...state,
      board: [...state.board],
      dice: state.dice
        ? { ...state.dice, movesRemaining: [...state.dice.movesRemaining] }
        : null,
      moveHistory: state.moveHistory.map(turn => [...turn]),
      borneOff: { ...state.borneOff },
      validMoves: [...state.validMoves],
    };
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  private hasCheckerAt(player: PlayerSide, board: BackgammonBoard, point: number): boolean {
    if (player === 'player1') return board[point] > 0;
    return board[point] < 0;
  }

  private canLandOn(player: PlayerSide, board: BackgammonBoard, point: number): boolean {
    if (point < 1 || point > 24) return false;
    const val = board[point];
    if (player === 'player1') {
      // Can land if empty, own checkers, or exactly 1 opponent checker (hit)
      return val >= -1;
    } else {
      return val <= 1;
    }
  }

  /**
   * Check if all of a player's checkers are in their home board.
   * P1 home board: points 1-6
   * P2 home board: points 19-24
   */
  private canBearOff(player: PlayerSide, board: BackgammonBoard): boolean {
    if (player === 'player1') {
      // No checkers on bar
      if (board[P1_BAR] > 0) return false;
      // No checkers on points 7-24
      for (let i = 7; i <= 24; i++) {
        if (board[i] > 0) return false;
      }
      return true;
    } else {
      if (board[P2_BAR] < 0) return false;
      for (let i = 1; i <= 18; i++) {
        if (board[i] < 0) return false;
      }
      return true;
    }
  }

  /**
   * For bearing off with a die higher than needed:
   * The checker must be on the highest occupied point in the home board.
   */
  private isHighestCheckerInHomeBoard(
    player: PlayerSide,
    board: BackgammonBoard,
    point: number
  ): boolean {
    if (player === 'player1') {
      // P1 home board: 1-6, highest = 6
      for (let i = 6; i > point; i--) {
        if (board[i] > 0) return false;
      }
      return true;
    } else {
      // P2 home board: 19-24, highest from P2 perspective = 19 (lowest number)
      for (let i = 19; i < point; i++) {
        if (board[i] < 0) return false;
      }
      return true;
    }
  }

  private createEmptyState(): BackgammonGameState {
    return {
      board: new Array(26).fill(0),
      currentPlayer: 'player1',
      dice: null,
      moveHistory: [],
      gameStatus: 'waiting',
      borneOff: { player1: 0, player2: 0 },
      turnNumber: 0,
      gameId: '',
      startedAt: 0,
      selectedPoint: null,
      validMoves: [],
    };
  }
}
